import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import apiClient from './apiClient';
import { articleStockKeys } from './useArticleStocks';
import { consumptionKeys } from './useConsumptions';
import { stockMovementKeys } from './useStockMovements';
import { inventoryArticleKeys } from './useInventoryArticles';
import { materialCostKeys } from './useMaterialCosts';
import { invalidateMaterialReports } from './useMaterialReports';

export const consumptionReturnKeys = {
  all: ['consumptionReturns'],
  lists: () => [...consumptionReturnKeys.all, 'list'],
  list: (filters = {}) => [...consumptionReturnKeys.lists(), filters],
  sources: (projectId) => [...consumptionReturnKeys.all, 'sources', projectId],
  details: () => [...consumptionReturnKeys.all, 'detail'],
  detail: (id) => [...consumptionReturnKeys.details(), id],
};

/**
 * Documentos de devolución de obra (pendientes, recibidos y anulados).
 * @param {Object} filters Filtros `uuid_project` y `status`.
 * @param {Object} [options] Opciones de TanStack Query (por ejemplo `enabled`).
 * @returns {Object} Query con la lista de devoluciones.
 */
export const useConsumptionReturns = (filters, options = {}) =>
  useQuery({
    queryKey: consumptionReturnKeys.list(filters),
    queryFn: async () => (await apiClient.get('consumptions/returns', { params: filters })).data,
    ...options,
  });

/**
 * Entregas con cantidades que todavía pueden devolverse desde una vivienda.
 * @param {string} projectId UUID de la vivienda de origen.
 * @returns {Object} Query con las entregas y su saldo sin comprometer.
 */
export const useConsumptionReturnSources = (projectId) =>
  useQuery({
    queryKey: consumptionReturnKeys.sources(projectId),
    enabled: Boolean(projectId),
    queryFn: async () => {
      const params = { uuid_project: projectId };
      return (await apiClient.get('consumptions/return-sources', { params })).data;
    },
  });

/**
 * Invalida lo que cambia cuando entra stock por una devolución recibida.
 * @param {Object} client QueryClient de TanStack Query.
 */
const invalidateReceivedReturn = (client) => {
  [articleStockKeys, stockMovementKeys, inventoryArticleKeys, materialCostKeys].forEach((keys) =>
    client.invalidateQueries({ queryKey: keys.all })
  );
  invalidateMaterialReports(client);
  client.invalidateQueries({ queryKey: consumptionKeys.all });
};

/**
 * Autoriza (sin `id`) o recibe (con `id`) una devolución. La autorización no mueve stock.
 * @returns {Object} Mutación que recibe `{ id?, payload }`.
 */
export const useSaveConsumptionReturn = () => {
  const client = useQueryClient();
  return useMutation({
    mutationFn: async ({ id, payload }) => {
      const url = id ? `consumptions/returns/${id}/receive` : 'consumptions/returns';
      return (await apiClient.post(url, payload)).data;
    },
    onSuccess: (_, variables) => {
      client.invalidateQueries({ queryKey: consumptionReturnKeys.all });
      if (variables.id) invalidateReceivedReturn(client);
    },
  });
};

/**
 * Anula una devolución pendiente de recepción; no mueve stock y libera el saldo autorizado.
 * @returns {Object} Mutación que recibe el UUID de la devolución.
 */
export const useCancelConsumptionReturn = () => {
  const client = useQueryClient();
  return useMutation({
    mutationFn: async (id) => (await apiClient.post(`consumptions/returns/${id}/cancel`)).data,
    onSuccess: () => {
      client.invalidateQueries({ queryKey: consumptionReturnKeys.all });
    },
  });
};
